import type { ChangeEvent } from "react";
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from "mobx";
import { inject, injectable } from "tsyringe";
import { ViewModel } from "@orderofchaos/mvvm";
import type { InputOnChangeData } from "@/components/Input/Input";
import {
  type BoxListItem,
  type IWarehouseApi,
  QrBindingConflictError,
  WAREHOUSE_API_TOKEN,
} from "@/api/WarehouseApi";
import type { IStore } from "@/stores/store";
import { STORE_TOKEN } from "@/stores/store";
import {
  type INavigationStore,
  NAVIGATION_STORE_TOKEN,
  Screens,
} from "@/stores/navigation";
import { Box } from "@/stores/Box";
import { normalizeTypeTags, resolveEntityTypeTags } from "@/utils/typeTags";
import {
  formatBoxReferenceInputValue,
  formatBoxReferenceLabel,
  resolveBoxReferenceInput,
} from "@/utils/boxReference";
import {
  extractQrPayload,
  normalizeQrPayloadInput,
  qrScanErrorToMessage,
  validateQrPayload,
} from "@/domain/qr-payload";
import { BOX_ID_PATTERN } from "@/domain/entity-ids";
import {
  prepareImageFileDataUrl,
  validateImageFile,
} from "@/utils/image-upload";

export interface EditBoxPageViewProps {
  box: Box;
}

function isValidUrl(value: string): boolean {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

@injectable()
export class EditBoxPageViewModel extends ViewModel<
  unknown,
  EditBoxPageViewProps
> {
  @observable
  name = "";

  @observable
  description = "";

  @observable
  photo = "";

  @observable
  typeTags: string[] = [];

  @observable
  typeTagInput = "";

  @observable
  parentId = "";

  @observable.shallow
  availableBoxes: BoxListItem[] = [];

  @observable.shallow
  typeTagSuggestions: string[] = [];

  @observable
  isReferenceLoading = false;

  @observable
  referenceError: string | null = null;

  @observable
  isPhotoLoading = false;

  @observable
  isSubmitting = false;

  @observable
  error: string | null = null;

  @observable
  boundQrPayload: string | null = null;

  @observable
  qrPayloadInput = "";

  @observable
  qrBindingError: string | null = null;

  @observable
  isQrScannerOpen = false;

  @observable
  isQrScanResolving = false;

  constructor(
    @inject(WAREHOUSE_API_TOKEN) private readonly api: IWarehouseApi,
    @inject(STORE_TOKEN) private readonly store: IStore,
    @inject(NAVIGATION_STORE_TOKEN)
    private readonly navigationStore: INavigationStore,
  ) {
    super();

    makeObservable(this);
  }

  @computed
  get box(): Box {
    return this.viewProps.box;
  }

  @computed
  get title(): string {
    return `редактируем ${this.name.trim() || "коробку"}`;
  }

  @computed
  get hasPhotoPreview(): boolean {
    return this.photo.trim().length > 0;
  }

  @computed
  get canSubmit(): boolean {
    return !this.isSubmitting;
  }

  @computed
  get qrScannerDelay(): number | false {
    if (!this.isQrScannerOpen || this.isSubmitting || this.isQrScanResolving) {
      return false;
    }

    return 300;
  }

  @computed
  get parentIdSuggestions(): Array<{ id: string; label: string }> {
    return this.availableBoxes.map((box) => ({
      id: box.id,
      label: formatBoxReferenceLabel(box),
    }));
  }

  protected onViewMounted(): void {
    this.resetFromBox();
    void this.loadBoundQrPayload();
    void this.loadReferenceData();
  }

  @action
  onNameChange = (
    event: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData,
  ): void => {
    this.name = data.value || event.target.value;
    this.error = null;
  };

  @action
  onDescriptionChange = (
    event: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData,
  ): void => {
    this.description = data.value || event.target.value;
    this.error = null;
  };

  @action
  onPhotoChange = (
    event: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData,
  ): void => {
    this.photo = data.value || event.target.value;
    this.error = null;
  };

  @action.bound
  async onPhotoFileChange(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const [file] = Array.from(event.target.files || []);
    event.target.value = "";

    if (!file) {
      return;
    }

    const validationError = validateImageFile(file);
    if (validationError) {
      this.error = validationError;
      return;
    }

    runInAction(() => {
      this.isPhotoLoading = true;
      this.error = null;
    });

    try {
      const dataUrl = await prepareImageFileDataUrl(file);
      runInAction(() => {
        this.photo = dataUrl;
      });
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Не удалось загрузить изображение";
      });
    } finally {
      runInAction(() => {
        this.isPhotoLoading = false;
      });
    }
  }

  @action
  setTypeTagInput = (value: string): void => {
    this.typeTagInput = value;
    this.error = null;
  };

  @action.bound
  addTypeTags(tags: string[]): void {
    this.typeTags = normalizeTypeTags([...this.typeTags, ...tags]);
    this.error = null;
  }

  @action.bound
  removeTypeTag(tag: string): void {
    this.typeTags = this.typeTags.filter((item) => item !== tag);
    this.error = null;
  }

  @action.bound
  setParentIdInput(value: string): void {
    this.parentId = value;
    this.error = null;
  }

  @action.bound
  toggleQrScanner(): void {
    if (this.isSubmitting) {
      return;
    }

    this.isQrScannerOpen = !this.isQrScannerOpen;
    this.qrBindingError = null;
  }

  @action.bound
  async onQrScan(rawData: string | null): Promise<void> {
    if (!rawData || !this.isQrScannerOpen || this.isQrScanResolving) {
      return;
    }

    const payload = extractQrPayload(rawData);
    if (!payload) {
      this.qrBindingError =
        "Сканируйте QR-код из партии печати (формат qr_...).";
      return;
    }

    runInAction(() => {
      this.isQrScanResolving = true;
      this.qrBindingError = null;
    });

    try {
      const existing = await this.api.resolveQrPayload(payload);
      if (existing && existing.entityId !== this.box.id) {
        runInAction(() => {
          this.qrPayloadInput = "";
          this.qrBindingError = this.formatQrConflictMessage(
            payload,
            existing.entityId,
          );
        });
        return;
      }

      runInAction(() => {
        this.qrPayloadInput = payload;
        this.qrBindingError = null;
        this.isQrScannerOpen = false;
      });
    } catch (error) {
      runInAction(() => {
        this.qrBindingError =
          error instanceof Error
            ? error.message
            : "Не удалось проверить QR-код";
      });
    } finally {
      runInAction(() => {
        this.isQrScanResolving = false;
      });
    }
  }

  @action.bound
  onQrScanError(error: unknown): void {
    this.qrBindingError = qrScanErrorToMessage(error);
  }

  @action.bound
  async loadReferenceData(): Promise<void> {
    runInAction(() => {
      this.isReferenceLoading = true;
      this.referenceError = null;
    });

    try {
      const boxes = await this.api.getAllBoxes();
      const tagSet = new Set<string>();

      boxes.forEach((box) => {
        resolveEntityTypeTags({
          typeTags: box.typeTags,
        }).forEach((tag) => tagSet.add(tag));
      });

      runInAction(() => {
        this.availableBoxes = boxes.filter((box) => box.id !== this.box.id);
        this.typeTagSuggestions = Array.from(tagSet).sort();
        this.parentId = this.formatParentInputValue(this.parentId);
      });
    } catch (error) {
      runInAction(() => {
        this.referenceError =
          error instanceof Error
            ? error.message
            : "Не удалось загрузить справочные данные";
      });
    } finally {
      runInAction(() => {
        this.isReferenceLoading = false;
      });
    }
  }

  @action
  resetFromBox = (): void => {
    this.name = this.box.name || "";
    this.description = this.box.description || "";
    this.photo = this.box.photo || "";
    this.typeTags = resolveEntityTypeTags({
      typeTags: this.box.typeTags,
    });
    this.typeTagInput = "";
    this.parentId = this.formatParentInputValue(this.box.parentId || "");
    this.error = null;
    this.isPhotoLoading = false;
    this.boundQrPayload = null;
    this.qrPayloadInput = "";
    this.qrBindingError = null;
    this.isQrScannerOpen = false;
    this.isQrScanResolving = false;
    void this.loadBoundQrPayload();
  };

  @action
  goBack = (): void => {
    this.navigationStore.setScreen(Screens.Box, this.box);
  };

  @action
  submit = async (): Promise<void> => {
    const validationError = this.validateForm();
    if (validationError) {
      this.error = validationError;
      return;
    }

    const nextQrPayload = normalizeQrPayloadInput(this.qrPayloadInput);
    if (nextQrPayload && !validateQrPayload(nextQrPayload)) {
      this.qrBindingError = "Укажите QR в формате qr_...";
      return;
    }

    if (nextQrPayload) {
      const isQrAvailable = await this.ensureQrPayloadAvailable(
        nextQrPayload,
        this.box.id,
      );
      if (!isQrAvailable) {
        return;
      }
    }

    this.qrBindingError = null;

    runInAction(() => {
      this.isSubmitting = true;
      this.error = null;
    });

    try {
      const normalizedParentId = this.resolveParentId();
      const updated = await this.api.updateBox(this.box.id, {
        name: this.name.trim(),
        description: this.description.trim(),
        photo: this.photo.trim(),
        typeTags: this.typeTags,
        parentId: normalizedParentId || null,
      });

      runInAction(() => {
        this.applyToBox(this.box, updated);
        const storeBox = this.store.boxes.find(
          (item) => item.id === this.box.id,
        );
        if (storeBox && storeBox !== this.box) {
          this.applyToBox(storeBox, updated);
        }
      });

      if (nextQrPayload) {
        try {
          const result = await this.api.bindQrPayload({
            entityId: this.box.id,
            payload: nextQrPayload,
          });
          runInAction(() => {
            this.boundQrPayload = result.payload;
            this.qrPayloadInput = "";
          });
        } catch (error) {
          if (error instanceof QrBindingConflictError) {
            runInAction(() => {
              this.qrBindingError = this.formatQrConflictMessage(
                nextQrPayload,
                error.existingEntityId,
              );
            });
            return;
          }

          runInAction(() => {
            this.qrBindingError =
              error instanceof Error
                ? error.message
                : "Не удалось привязать QR-код к коробке";
          });
          return;
        }
      }

      this.navigationStore.setScreen(Screens.Box, this.box);
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Не удалось сохранить изменения коробки";
      });
    } finally {
      runInAction(() => {
        this.isSubmitting = false;
      });
    }
  };

  private async loadBoundQrPayload(): Promise<void> {
    try {
      const binding = await this.api.getQrBindingByEntity(this.box.id);
      runInAction(() => {
        this.boundQrPayload = binding?.payload ?? null;
      });
    } catch {
      runInAction(() => {
        this.boundQrPayload = null;
      });
    }
  }

  private async ensureQrPayloadAvailable(
    payload: string,
    allowEntityId: string | null,
  ): Promise<boolean> {
    try {
      const existing = await this.api.resolveQrPayload(payload);
      if (!existing || existing.entityId === allowEntityId) {
        return true;
      }

      this.qrBindingError = this.formatQrConflictMessage(
        payload,
        existing.entityId,
      );
      return false;
    } catch (error) {
      this.qrBindingError =
        error instanceof Error ? error.message : "Не удалось проверить QR-код";
      return false;
    }
  }

  private formatQrConflictMessage(
    payload: string,
    entityId: string | null,
  ): string {
    if (!entityId) {
      return `QR ${payload} уже используется другой сущностью.`;
    }

    return `QR ${payload} уже привязан к ${entityId}. Выберите другой код.`;
  }

  private validateForm(): string | null {
    if (!this.name.trim()) {
      return "Укажите название коробки";
    }

    if (this.typeTags.length === 0) {
      return "Добавьте хотя бы один тег типа";
    }

    const parentId = this.resolveParentId();
    if (parentId && !BOX_ID_PATTERN.test(parentId)) {
      return "Выберите существующую коробку из списка";
    }

    if (parentId && parentId === this.box.id) {
      return "Коробка не может быть родителем самой себе";
    }

    const photo = this.photo.trim();
    if (photo && !isValidUrl(photo)) {
      return "Ссылка на фото должна быть корректным URL";
    }

    return null;
  }

  private applyToBox(target: Box, updated: BoxListItem): void {
    target.name = updated.name;
    target.description = updated.description || "";
    target.photo = updated.photo || "";
    target.typeTags = normalizeTypeTags(updated.typeTags);
    target.parentId = updated.parentId;
  }

  private resolveParentId(): string {
    return resolveBoxReferenceInput(this.parentId, this.availableBoxes);
  }

  private formatParentInputValue(value: string): string {
    return formatBoxReferenceInputValue(value, this.availableBoxes);
  }
}
